import { skillsOf } from './tabs/skills';
import { SkillTier } from '../janus-engine/port/k5-render/catalog';
import type { CachedCaster } from './types';
import { catalogDefinition, sheetDefinition } from './k5-runtime';
import { craftingPlan } from '../janus-engine/actions/ability-preconditions';
import { resolveCraftingStock, type CraftStartReaders } from '../janus-engine/actions/crafting-start';
import { uuidOf } from './tabs/ability-row';
import { at, values } from '../janus-engine/port/k5-render/support';
import { num } from './read-helpers';

type Row = Record<string, unknown>;
const craftingMemo = new WeakMap<CachedCaster, { inputs: Row; revision: unknown[]; payload: Row }>();
interface CraftingNeeds { definitions: Set<string>; stock: boolean; floor: boolean }
const craftingEncounter = (caster: CachedCaster): Row | null => (window.FateTide?.encounterCache?.get(
    String(ftEncounterCreatureField(caster.encounter_creatures, 'encounter_id') ?? '')) ?? null) as Row | null;
const craftingSourceRevision = (caster: CachedCaster, encounter = craftingEncounter(caster)): number[] => [num(caster.base_rev),
    num(ftEncounterCreatureField(caster.encounter_creatures, 'movement_rev')), num(encounter?.base_rev)];
export function sheetCraftingPayload(caster: CachedCaster, inputs: Row): Row { return craftingPayload(caster, inputs); }
function craftingPayload(caster: CachedCaster, inputs: Row, needs?: CraftingNeeds): Row {
    const encounter = craftingEncounter(caster);
    const revision = [...craftingSourceRevision(caster, encounter), caster.item_arr_last_t_updated, ftEncounterCreatureField(caster.encounter_creatures, 'encounter_creature_t_updated'),
        caster.computed, caster.catalog, caster.inventory, caster.statuses, encounter, encounter?.encounter_inventories_arr,
        ftHasPlayerPermission(caster)];
    const memo = craftingMemo.get(caster);
    if (!needs && memo?.inputs === inputs && memo.revision.every((value, index) => value === revision[index])) return memo.payload;
    const resolve = (entry: unknown): Row => {
        const id = uuidOf(entry);
        return catalogDefinition(inputs.catalog, id) ?? sheetDefinition(caster, entry);
    };
    const read: CraftStartReaders = {
        value: (row, key) => ftLayerValue(row, key) ?? null,
        has: (row, key) => ftLayerHas(row, key),
        entries: (row, key) => ftLayerEntries(row, key) ?? [],
        inventoryField: (row, key) => ftInventoryField(row, key) ?? null,
        statuses: () => [], inCombat: () => false,
        resolveItem: entry => { try { return resolve(entry); } catch {
            const id = uuidOf(entry).toUpperCase();
            if (/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id)) needs?.definitions.add(id);
            return null;
        } },
        name: id => String(ftItemsCol(read.resolveItem?.(id), 'item_name') ?? '') || null,
    };
    const stock = (id: string, qty: number, template: string | null = null, floor = true) => {
        const result = resolveCraftingStock(read, caster, encounter, { id, qty, template }, floor);
        if (needs && result.status === 'unknown') {
            needs.stock = true;
            if (floor && (Array.isArray(caster.inventory) || !Array.isArray(encounter?.encounter_inventories_arr ?? caster.encounter_inventory))) needs.floor = true;
        }
        return result;
    };
    const ids = values(inputs.ids).map(String);
    const casterSkills = skillsOf(caster);
    const skills = values(inputs.skills).flatMap(raw => {
        const variable = String(at(raw, 'variable_name') ?? '');
        const tier = num(casterSkills[variable]?.tier_level);
        if (tier <= 0) return [];
        const types = values(at(raw, 'recipe_names') ?? [at(raw, 'recipe_name')]).map(String);
        const recipes = ids.flatMap(id => {
            const candidate = read.resolveItem?.(id);
            if (!candidate) {
                if (needs) return [];
                throw new Error(`unported:crafting-display:definition-not-held:${id}`);
            }
            if (!types.includes(String(ftItemsCol(candidate, 'item_type') ?? ''))) return [];
            const recipe = resolve(id);
            const required = SkillTier.map(row => row.skill_tier_level).find(level => ftLayerHas(recipe, `requires_skill_tier_${level}`)) ?? 0;
            if (tier < required) return [];
            const tools = ftLayerEntries(recipe, 'requires_tool').map(entry => {
                const toolId = uuidOf(entry), result = stock(toolId, 1, null, false);
                return { name: read.name?.(toolId) ?? toolId, missing: result.status !== 'ok', unknown: result.status === 'unknown' };
            });
            const build = (size: string) => {
                const plan = craftingPlan(recipe, size, read);
                if (!plan || 'refuse' in plan) return { reagents: [], crafting_time_int: 0, crafting_time_type: 'seconds', can_craft: false,
                    refusal: `unported:crafting-display:plan:${plan && 'refuse' in plan ? plan.refuse : 'definition-not-held'}` };
                let unknown = false;
                const reagents = plan.manifest.map(requirement => {
                    const result = stock(requirement.id, requirement.qty, requirement.template);
                    if (result.status === 'unknown') unknown = true;
                    return { label: `${requirement.qty} × ${read.name?.(requirement.id) ?? requirement.id}`,
                        missing: result.status !== 'ok', magical: requirement.template !== null };
                });
                return { reagents, crafting_time_int: plan.requiredSeconds, crafting_time_type: 'seconds',
                    can_craft: tools.every(tool => !tool.missing) && reagents.every(reagent => !reagent.missing),
                    refusal: tools.some(tool => tool.unknown) ? 'unported:crafting-display:tool-stock-not-held' : unknown ? 'unported:crafting-display:stock-not-held' : '', plan_ok: true };
            };
            const base = build('');
            const outputEntry = ftLayerEntries(recipe, 'fabricate')[0];
            let defaultSize = '';
            defaultSize = String(ftLayerValue(read.resolveItem?.(uuidOf(outputEntry)), 'size') ?? '');
            const scalable = num(ftLayerValue(recipe, 'size_scalable')) !== 0;
            const sizes = scalable ? values(inputs.sizes).map(size => ({ size_id: String(at(size, 'item_id')), name: String(at(size, 'item_name')),
                is_default: String(at(size, 'item_id')) === defaultSize, ...build(String(at(size, 'item_id'))) })) : [];
            return [{ id, name: String(ftItemsCol(recipe, 'item_name') ?? ''), item_type: String(ftItemsCol(recipe, 'item_type') ?? ''),
                required_tier: required, ability_dc: ftLayerValue(recipe, 'ability_dc'), tools, tools_ok: tools.every(tool => !tool.missing),
                scalable, default_size: defaultSize, sizes, ...base }];
        });
        return [{ var: variable, name: String(at(raw, 'item_name') ?? variable), tab_icon_id: String(at(raw, 'item_id') ?? ''),
            col_icon_id: String(at(raw, 'item_id') ?? ''), tier_level: tier, recipes }];
    });
    const payload = { skills, can_edit: ftHasPlayerPermission(caster) };
    if (!needs) craftingMemo.set(caster, { inputs, revision, payload });
    return payload;
}

interface CraftingRead { revision: number[]; result: Promise<void>; pending: boolean }
const craftingReads = new WeakMap<Row, Map<string, CraftingRead>>();
/** One hydration round per source revision; repaints during a caster read join the pending round. */
export async function hydrateSheetCraftingPayload(caster: CachedCaster, inputs: Row): Promise<Row> {
    const actorId = String(ftEncounterCreatureField(caster.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
    let reads = craftingReads.get(inputs);
    if (!reads) { reads = new Map(); craftingReads.set(inputs, reads); }
    const pending = reads.get(actorId);
    const revision = craftingSourceRevision(caster);
    if (pending && (pending.pending || pending.revision.every((value, index) => value === revision[index]))) return pending.result.then(() => {
        const fresh = (window.FateTide?.casterCache?.get(actorId) ?? caster) as CachedCaster;
        craftingMemo.delete(fresh);
        return sheetCraftingPayload(fresh, inputs);
    });
    const needs: CraftingNeeds = { definitions: new Set(), stock: false, floor: false };
    craftingPayload(caster, inputs, needs);
    if (!needs.definitions.size && !needs.stock) return Promise.resolve(sheetCraftingPayload(caster, inputs));
    const api = window as Window & { ftHydrateCraftingFacts?: (facts: { actorId?: string; encounterId?: string; definitionIds: string[] }) => Promise<void> };
    const state: CraftingRead = { revision, result: Promise.resolve(), pending: true };
    const request = Promise.resolve().then(async () => {
        try {
            await api.ftHydrateCraftingFacts?.({ actorId: needs.stock ? actorId : undefined,
                encounterId: needs.floor ? String(ftEncounterCreatureField(caster.encounter_creatures, 'encounter_id') ?? '') : undefined,
                definitionIds: [...needs.definitions].slice(0, 32) });
        } catch { /* A still-unheld fact keeps the original display refusal after the single retry. */ }
        const fresh = (window.FateTide?.casterCache?.get(actorId) ?? caster) as CachedCaster;
        craftingMemo.delete(caster); craftingMemo.delete(fresh);
        return sheetCraftingPayload(fresh, inputs);
    }).finally(() => {
        state.pending = false;
        state.revision = craftingSourceRevision((window.FateTide?.casterCache?.get(actorId) ?? caster) as CachedCaster);
    });
    state.result = request.then(() => undefined, () => undefined);
    reads.set(actorId, state);
    // Retain the attempt, including a failed read: heartbeats cannot restart the fetch indefinitely.
    return request;
}
